import { useEffect, useState } from 'react';

/**
 * Shown while routes are being built.
 *
 * The solver works in the background and the board updates itself when it is
 * done, but a button that only turns into a spinner reads as nothing
 * happening. This says plainly that work is under way, roughly what stage it
 * is at, and how long it has taken -- and it covers the board, because
 * dragging stops around while the day is being rebuilt underneath would be
 * lost the moment the new plan lands.
 *
 * The stages are paced, not reported: the solver does not stream its
 * progress. They are in the order the work really happens, and the last one
 * holds until the plan arrives rather than claiming to finish early.
 */

const STAGES = [
  'Reading orders and vehicles',
  'Working out travel times',
  'Assigning stops to vehicles',
  'Sequencing each route',
  'Timing every stop',
  'Saving the plan',
];

/* Seconds before each stage starts. The last holds until the plan arrives. */
const AT = [0, 2, 5, 9, 14, 20];
const SLOW_AFTER = 120;

export function BuildingOverlay({
  startedAt,
  orders,
  vehicles,
  onHide,
}: {
  startedAt: number;
  orders: number;
  vehicles: number;
  onHide: () => void;
}) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  let stage = 0;
  for (let i = 0; i < AT.length; i += 1) if (seconds >= AT[i]!) stage = i;
  const slow = seconds >= SLOW_AFTER;

  return (
    <div className="build-overlay" role="alertdialog" aria-modal="true" aria-labelledby="build-title" aria-describedby="build-stage">
      <div className="build-card">
        <svg className="build-art" viewBox="0 0 220 90" aria-hidden="true">
          <defs>
            <linearGradient id="build-route" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor="#13c9b4" />
              <stop offset="1" stopColor="#7c5cff" />
            </linearGradient>
          </defs>
          <path className="build-track" d="M14 66 C 50 10, 80 80, 112 44 S 170 12, 206 30" />
          <path className="build-path" d="M14 66 C 50 10, 80 80, 112 44 S 170 12, 206 30" stroke="url(#build-route)" />
          <circle className="build-stop" style={{ animationDelay: '0s' }} cx="14" cy="66" r="6" />
          <circle className="build-stop" style={{ animationDelay: '0.45s' }} cx="68" cy="44" r="5" />
          <circle className="build-stop" style={{ animationDelay: '0.9s' }} cx="112" cy="44" r="5" />
          <circle className="build-stop" style={{ animationDelay: '1.35s' }} cx="160" cy="24" r="5" />
          <circle className="build-stop build-stop-end" style={{ animationDelay: '1.8s' }} cx="206" cy="30" r="6" />
        </svg>

        <h2 id="build-title">Building routes</h2>
        <p className="build-scope">
          {orders > 0 ? `${orders} order${orders === 1 ? '' : 's'}` : 'Your orders'}
          {vehicles > 0 ? ` across ${vehicles} vehicle${vehicles === 1 ? '' : 's'}` : ''}
        </p>

        <ol className="build-stages">
          {STAGES.map((label, i) => (
            <li key={label} data-state={i < stage ? 'done' : i === stage ? 'now' : 'next'}>
              <span className="build-dot" aria-hidden="true" />
              {label}
            </li>
          ))}
        </ol>

        <p id="build-stage" className="build-status" aria-live="polite">
          {slow
            ? 'This is taking longer than usual. It is still running, and the board will update when it is done.'
            : `${STAGES[stage]}…`}
        </p>

        <div className="build-foot">
          <span className="build-elapsed">{formatElapsed(seconds)}</span>
          {slow ? (
            <button type="button" className="btn" onClick={onHide}>
              Hide and keep working
            </button>
          ) : (
            <span className="dim small">The board updates by itself when it is done.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function formatElapsed(s: number): string {
  const m = Math.floor(s / 60);
  return m ? `${m}:${String(s % 60).padStart(2, '0')}` : `${s}s`;
}
